import { useEffect, useState } from "react";
import { Mail, Reply, MessageCircle, Copy, ArrowLeft, Check, Loader2, Sparkles, Eye, Zap, Shield, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import jsPDF from "jspdf";
import rewardfulLogo from "@/assets/rewardful-logo.png";

const REWARDFUL_FOOTER = "Generated with Rewardful — launch your affiliate program in minutes: https://www.rewardful.com";

type Templates = {
  intel: {
    product: string;
    category: string;
    commission: string;
    idealAffiliates: string[];
    dealScore: number;
  };
  coldEmail: { subject: string; body: string };
  followUp: { subject: string; body: string; timing?: string };
  linkedIn: { body: string };
  copyNotes: string;
};

const LOADING_STAGES = [
  { label: "Reading your homepage…", ms: 2200 },
  { label: "Identifying your ideal partners…", ms: 2600 },
  { label: "Drafting your templates…", ms: 4000 },
  { label: "Polishing the copy…", ms: 3000 },
];

const EXAMPLE: Templates = {
  intel: {
    product: "Linear",
    category: "B2B SaaS — Project management for software teams",
    commission: "30% recurring for 12 months",
    idealAffiliates: [
      "Engineering leadership newsletter writers (Lenny / Pragmatic Engineer style)",
      "Indie hacker YouTubers reviewing dev productivity tools",
      "Agencies setting up workflows for early-stage startup clients",
    ],
    dealScore: 8,
  },
  coldEmail: {
    subject: "Linear x [their newsletter]",
    body: `Hi [First Name],\n\n[Their newsletter] reaches exactly the people who feel the pain Linear was built for — engineering leads at fast-moving startups rethinking how their team ships.\n\nI run partnerships at Linear (project tracker for software teams, used by Vercel, Ramp, and Cash App). We just opened our affiliate program and your audience is a clean fit.\n\nThe deal: 30% recurring for 12 months on every team you refer. Stripe-based tracking, monthly payouts, no caps.\n\nWorth a 10-minute call to see if it's a fit?\n\n[Your Name]\nLinear partnerships`,
  },
  followUp: {
    subject: "Re: Linear x [their newsletter]",
    body: `Hi [First Name], floating this back up. Happy to send a 2-minute Loom of the affiliate dashboard so you can see how attribution and payouts work before deciding anything. Just say the word.\n\n[Your Name]`,
    timing: "Send 5–7 days after the cold email",
  },
  linkedIn: {
    body: `Hi [First Name] — your audience at [their newsletter / channel] looks like an unusually clean fit for Linear's new affiliate program (30% recurring, 12 months). Open to a quick intro?`,
  },
  copyNotes: `Replace [First Name], [their newsletter / channel], and [Your Name] before sending. The Linear product details, social proof, and commission terms are already locked in — leave those as-is.`,
};

function buildMarkdown(t: Templates): string {
  const lines: string[] = [];
  lines.push(`# Affiliate Outreach — ${t.intel?.product ?? "Your Product"}`, "");
  if (t.intel) {
    lines.push("## Affiliate Intel", "");
    lines.push(`- **Product:** ${t.intel.product}`);
    lines.push(`- **Category:** ${t.intel.category}`);
    lines.push(`- **Commission:** ${t.intel.commission}`);
    lines.push(`- **Deal Score:** ${t.intel.dealScore}/10`);
    lines.push("", "**Ideal Affiliates:**");
    t.intel.idealAffiliates.forEach((a) => lines.push(`- ${a}`));
    lines.push("");
  }
  lines.push("## Cold Email", "", `**Subject:** ${t.coldEmail.subject}`, "", t.coldEmail.body, "");
  lines.push("## Follow-Up", "");
  if (t.followUp.timing) lines.push(`_${t.followUp.timing}_`, "");
  lines.push(`**Subject:** ${t.followUp.subject}`, "", t.followUp.body, "");
  lines.push("## LinkedIn DM", "", t.linkedIn.body, "");
  if (t.copyNotes) lines.push("## Before You Send", "", t.copyNotes, "");
  lines.push("---", "", `_${REWARDFUL_FOOTER}_`);
  return lines.join("\n");
}

function buildPlainText(t: Templates): string {
  return buildMarkdown(t)
    .replace(/^#+\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/_(.+?)_/g, "$1")
    .replace(/^- /gm, "• ");
}

function fileSlug(t: Templates): string {
  const slug = (t.intel?.product ?? "outreach").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return slug || "outreach";
}

function downloadBlob(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportPdf(t: Templates) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const margin = 54;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const maxW = pageW - margin * 2;
  let y = margin;

  const writeBlock = (text: string, size: number, style: "normal" | "bold" = "normal", gap = 6) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text, maxW);
    lines.forEach((ln: string) => {
      if (y + size + 4 > pageH - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(ln, margin, y);
      y += size + 4;
    });
    y += gap;
  };

  // Small Rewardful logo in top-right of first page
  try {
    const logoW = 80;
    const logoH = (92 / 417) * logoW;
    doc.addImage(rewardfulLogo, "PNG", pageW - margin - logoW, margin - logoH / 2, logoW, logoH);
  } catch {
    // ignore if image fails
  }

  writeBlock(`Affiliate Outreach — ${t.intel?.product ?? "Your Product"}`, 18, "bold", 12);
  if (t.intel) {
    writeBlock("Affiliate Intel", 14, "bold", 6);
    writeBlock(`Product: ${t.intel.product}`, 11);
    writeBlock(`Category: ${t.intel.category}`, 11);
    writeBlock(`Commission: ${t.intel.commission}`, 11);
    writeBlock(`Deal Score: ${t.intel.dealScore}/10`, 11);
    writeBlock("Ideal Affiliates:", 11, "bold", 2);
    t.intel.idealAffiliates.forEach((a) => writeBlock(`• ${a}`, 11, "normal", 2));
    y += 6;
  }
  writeBlock("Cold Email", 14, "bold", 6);
  writeBlock(`Subject: ${t.coldEmail.subject}`, 11, "bold");
  writeBlock(t.coldEmail.body, 11);
  writeBlock("Follow-Up", 14, "bold", 6);
  if (t.followUp.timing) writeBlock(t.followUp.timing, 10);
  writeBlock(`Subject: ${t.followUp.subject}`, 11, "bold");
  writeBlock(t.followUp.body, 11);
  writeBlock("LinkedIn DM", 14, "bold", 6);
  writeBlock(t.linkedIn.body, 11);
  if (t.copyNotes) {
    writeBlock("Before You Send", 14, "bold", 6);
    writeBlock(t.copyNotes, 11);
  }

  // Footer on every page
  const pageCount = doc.getNumberOfPages();
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footerLines = doc.splitTextToSize(REWARDFUL_FOOTER, maxW);
    doc.text(footerLines, margin, pageH - margin / 2);
  }

  doc.save(`${fileSlug(t)}-templates.pdf`);
}

const Index = () => {
  const [url, setUrl] = useState("");
  const [commission, setCommission] = useState("");
  const [loading, setLoading] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [templates, setTemplates] = useState<Templates | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showExample, setShowExample] = useState(false);

  // Cycle loading stages
  useEffect(() => {
    if (!loading) {
      setStageIdx(0);
      return;
    }
    let i = 0;
    const tick = () => {
      i = Math.min(i + 1, LOADING_STAGES.length - 1);
      setStageIdx(i);
      if (i < LOADING_STAGES.length - 1) {
        timeout = window.setTimeout(tick, LOADING_STAGES[i].ms);
      }
    };
    let timeout = window.setTimeout(tick, LOADING_STAGES[0].ms);
    return () => window.clearTimeout(timeout);
  }, [loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const parsed = new URL(url.trim());
      if (!["http:", "https:"].includes(parsed.protocol)) throw new Error();
    } catch {
      setError("Please enter a valid URL starting with http:// or https://");
      return;
    }
    if (!commission.trim()) {
      setError("Please enter your commission offer.");
      return;
    }

    setLoading(true);
    setTemplates(null);
    setShowExample(false);

    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "generate-outreach",
        { body: { url: url.trim(), commission: commission.trim() } },
      );

      if (fnError) {
        const ctx = (fnError as { context?: { status?: number } }).context;
        if (ctx?.status === 429) throw new Error("Rate limit hit. Please try again in a moment.");
        if (ctx?.status === 402) throw new Error("AI credits exhausted. Add credits in workspace settings.");
        throw new Error(fnError.message || "Generation failed");
      }
      if (!data?.templates) throw new Error("No templates returned");

      setTemplates(data.templates as Templates);
      setTimeout(() => {
        document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setError(msg);
      toast({ title: "Generation failed", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const displayed = templates ?? (showExample ? EXAMPLE : null);
  const isExample = !templates && showExample;

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur border-b border-brand-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center" aria-label="Rewardful">
            <img src={rewardfulLogo} alt="Rewardful Logo" className="h-7 w-auto" />
          </a>
          <a
            href="https://rewardful.com"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-brand-navy transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Rewardful.com
          </a>
        </div>
      </header>

      <main id="main" role="main" className="flex-1">
        {/* Hero */}
        <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-8 text-center">
          <span className="inline-flex items-center gap-1.5 bg-brand-yellow text-brand-navy text-xs font-semibold px-3 py-1 rounded-full mb-5 tracking-wide uppercase">
            <Sparkles className="h-3.5 w-3.5" />
            Free AI Tool
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-brand-navy leading-[1.05]">
            Affiliate Outreach Generator
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Drop your product URL. Get three outreach templates ready to personalize and send — cold email, follow-up, and LinkedIn DM.
          </p>

          {/* Trust strip */}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-brand-blue" />
              Generates in ~15s
            </span>
            <span className="hidden sm:inline text-brand-border">•</span>
            <span className="inline-flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-brand-green" />
              No signup required
            </span>
            <span className="hidden sm:inline text-brand-border">•</span>
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-brand-navy" />
              Reads your real homepage
            </span>
          </div>
        </section>

        {/* Generator Form */}
        <section className="max-w-2xl mx-auto px-5 sm:px-8 pb-10">
          <div className="bg-card border border-brand-border rounded-2xl shadow-sm p-6 sm:p-8">
            <form className="space-y-5" onSubmit={handleSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="url" className="text-brand-navy font-medium">
                  Your Product URL
                </Label>
                <Input
                  id="url"
                  type="url"
                  required
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://yourproduct.com"
                  className="h-12"
                  disabled={loading}
                  maxLength={500}
                />
                <p className="text-sm text-muted-foreground">
                  We'll read your homepage so you don't have to describe it.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="commission" className="text-brand-navy font-medium">
                  Commission Offer
                </Label>
                <Input
                  id="commission"
                  type="text"
                  required
                  value={commission}
                  onChange={(e) => setCommission(e.target.value)}
                  placeholder="e.g. 30% recurring for 12 months"
                  className="h-12"
                  disabled={loading}
                  maxLength={200}
                />
                <p className="text-sm text-muted-foreground">
                  Be specific. Affiliates filter by economics first.
                </p>
              </div>


              {error && (
                <p className="text-sm font-medium text-destructive" role="alert">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-semibold bg-brand-blue hover:bg-brand-blue/90 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  "Generate Templates"
                )}
              </Button>

              {!loading && !templates && (
                <button
                  type="button"
                  onClick={() => {
                    setShowExample((s) => !s);
                    if (!showExample) {
                      setTimeout(() => {
                        document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 50);
                    }
                  }}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-brand-blue transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  {showExample ? "Hide example output" : "See an example first"}
                </button>
              )}
            </form>

            {/* Loading stages */}
            {loading && (
              <div className="mt-6 pt-6 border-t border-brand-border space-y-2 fade-in">
                {LOADING_STAGES.map((stage, i) => (
                  <div key={stage.label} className="flex items-center gap-2.5 text-sm">
                    {i < stageIdx ? (
                      <Check className="h-4 w-4 text-brand-green shrink-0" />
                    ) : i === stageIdx ? (
                      <Loader2 className="h-4 w-4 text-brand-blue animate-spin shrink-0" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-brand-border shrink-0" />
                    )}
                    <span className={i <= stageIdx ? "text-brand-navy" : "text-muted-foreground"}>
                      {stage.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Results */}
        {displayed && (
          <section id="results" className="max-w-3xl mx-auto px-5 sm:px-8 pb-20">
            {isExample && (
              <div className="mb-5 flex items-center justify-between gap-3 px-4 py-3 bg-brand-yellow/40 border border-brand-yellow rounded-xl">
                <p className="text-sm text-brand-navy font-medium">
                  This is an example. Generate your own above for templates tailored to your product.
                </p>
                <button
                  type="button"
                  onClick={() => setShowExample(false)}
                  className="text-sm text-brand-navy/70 hover:text-brand-navy font-medium shrink-0"
                >
                  Hide
                </button>
              </div>
            )}
            <div className="mb-5 flex items-center justify-end">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <Download className="h-4 w-4" />
                    Export
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-44 p-1">
                  <button
                    type="button"
                    onClick={() => downloadBlob(`${fileSlug(displayed)}-templates.md`, buildMarkdown(displayed), "text/markdown")}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent text-brand-navy"
                  >
                    Markdown (.md)
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadBlob(`${fileSlug(displayed)}-templates.txt`, buildPlainText(displayed), "text/plain")}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent text-brand-navy"
                  >
                    Plain text (.txt)
                  </button>
                  <button
                    type="button"
                    onClick={() => exportPdf(displayed)}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent text-brand-navy"
                  >
                    PDF (.pdf)
                  </button>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-5">
              {/* Affiliate Intel */}
              {displayed.intel && (
              <div className="bg-card border border-brand-border rounded-2xl p-6 shadow-sm fade-in">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="text-brand-navy font-semibold text-lg">Affiliate Intel</h2>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-blue/10 text-brand-blue text-xs font-semibold">
                    Deal Score {displayed.intel.dealScore}/10
                  </span>
                </div>
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Product</dt>
                    <dd className="text-brand-navy font-medium">{displayed.intel.product}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Category</dt>
                    <dd className="text-brand-navy font-medium">{displayed.intel.category}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-muted-foreground">Commission</dt>
                    <dd className="text-brand-navy font-medium">{displayed.intel.commission}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-muted-foreground mb-1">Ideal Affiliates</dt>
                    <dd>
                      <ul className="space-y-1 list-disc list-inside text-brand-navy/90">
                        {displayed.intel.idealAffiliates.map((p, i) => (
                          <li key={i}>{p}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                </dl>
              </div>
              )}

              {displayed.copyNotes && (
              <div className="bg-brand-yellow/60 border border-brand-yellow rounded-2xl p-6">
                <h2 className="text-brand-navy font-semibold text-lg mb-2">
                  Before You Send
                </h2>
                <p className="text-brand-navy/90 text-sm leading-relaxed">
                  {displayed.copyNotes}
                </p>
              </div>
              )}

              <ResultCard
                icon={<Mail className="h-5 w-5" />}
                label="Cold Email"
                subject={displayed.coldEmail.subject}
                body={displayed.coldEmail.body}
              />
              <ResultCard
                icon={<Reply className="h-5 w-5" />}
                label="Follow-Up"
                note={displayed.followUp.timing || "Send 5–7 days after the first email if no reply"}
                subject={displayed.followUp.subject}
                body={displayed.followUp.body}
              />
              <ResultCard
                icon={<MessageCircle className="h-5 w-5" />}
                label="LinkedIn DM"
                body={displayed.linkedIn.body}
              />
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="bg-brand-navy text-white">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-20 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight">
              Now Track Every Affiliate You Recruit
            </h2>
            <p className="mt-4 text-white/75 text-lg max-w-2xl mx-auto">
              Templates are step one. Rewardful handles the rest — tracking, commissions, and payouts through Stripe or Paddle. Trusted by 2,600+ SaaS companies.
            </p>
            <a
              href="https://app.getrewardful.com/signup?utm_source=outreach_generator&utm_medium=tool&utm_campaign=trial_conversion&utm_content=cta_card"
              className="inline-flex items-center justify-center mt-8 h-12 px-7 rounded-lg bg-brand-green hover:bg-brand-green/90 text-white font-semibold transition-colors"
            >
              Start Your 14-Day Free Trial
            </a>
            <p className="mt-4 text-sm text-white/60">
              Stripe Verified Partner · 0% transaction fee · No credit card required
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-brand-border bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-muted-foreground">
          <p>Built by Rewardful — the easiest way to launch a SaaS affiliate program</p>
          <a
            href="https://www.rewardful.com"
            className="flex items-center hover:opacity-80 transition-opacity"
            aria-label="Rewardful"
          >
            <img src={rewardfulLogo} alt="Rewardful Logo" className="h-6 w-auto" />
          </a>
        </div>
      </footer>
    </div>
  );
};

type ResultCardProps = {
  icon: React.ReactNode;
  label: string;
  subject?: string;
  body: string;
  note?: string;
};

const ResultCard = ({ icon, label, subject, body, note }: ResultCardProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = subject ? `Subject: ${subject}\n\n${body}` : body;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({ title: `${label} copied`, description: "Paste it into your email or DM." });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Please copy manually.", variant: "destructive" });
    }
  };

  return (
    <article className="bg-card border border-brand-border rounded-2xl p-6 shadow-sm fade-in">
      <header className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2 text-brand-navy">
          <span className="text-brand-blue">{icon}</span>
          <h3 className="font-semibold">{label}</h3>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-brand-blue transition-colors"
        >
          {copied ? (
            <>
              <Check className="h-4 w-4 text-brand-green" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              Copy
            </>
          )}
        </button>
      </header>

      {note && (
        <p className="text-xs italic text-muted-foreground mb-3">{note}</p>
      )}

      {subject && (
        <div className="mb-3 px-3 py-2 rounded-md bg-brand-bg border border-brand-border text-sm">
          <span className="text-muted-foreground">Subject: </span>
          <span className="text-brand-navy font-medium">{subject}</span>
        </div>
      )}

      <p className="whitespace-pre-line text-brand-navy/90 leading-relaxed text-[15px]">
        {body}
      </p>
    </article>
  );
};

export default Index;
